import React, { useState, useEffect } from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import TimezoneSelect from 'react-timezone-select';
import Notification from './Notification';
import buddyToolsData from '../data/buddyTools';
import './Profile.css';

// Custom styles for react-timezone-select to handle dark mode
const customTimezoneStyles = {
  control: (provided) => ({
    ...provided,
    backgroundColor: 'var(--color-background)',
    borderColor: 'var(--color-border)',
    color: 'var(--color-text)',
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'var(--color-cardBackground)',
    borderColor: 'var(--color-border)',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? 'var(--color-primary)' : 'var(--color-cardBackground)',
    color: state.isFocused ? '#FFFFFF' : 'var(--color-text)',
    '&:hover': {
      backgroundColor: 'var(--color-primary)',
      color: '#FFFFFF',
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: 'var(--color-text)',
  }),
  input: (provided) => ({
    ...provided,
    color: 'var(--color-text)',
  }),
};

const Profile = () => {
  const { user, isLoading } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    jobTitle: '',
    phoneNumber: '',
  });

  // Preference state
  const [preferences, setPreferences] = useState({
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    appearance: 'system',
    uiDensity: 'comfortable',
    dateFormat: 'MM/DD/YYYY',
    clockFormat: '12h',
    sidebarBehavior: 'expanded',
    defaultLandingTool: 'home',
    accessibilityFontSize: 'medium',
    doNotSell: false,
  });

  // Original state for cancellation
  const [originalData, setOriginalData] = useState({});
  const [originalPreferences, setOriginalPreferences] = useState({});

  // Notification state
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  useEffect(() => {
    if (user) {
      const initialData = {
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        jobTitle: '',
        phoneNumber: '',
      };
      const initialPrefs = {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        appearance: 'system',
        uiDensity: 'comfortable',
        dateFormat: 'MM/DD/YYYY',
        clockFormat: '12h',
        sidebarBehavior: 'expanded',
        defaultLandingTool: 'home',
        accessibilityFontSize: 'medium',
        doNotSell: false,
      };
      setFormData(initialData);
      setOriginalData(initialData);
      setPreferences(initialPrefs);
      setOriginalPreferences(initialPrefs);
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePreferenceChange = (e) => {
    const { name, value } = e.target;
    setPreferences((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({ ...prev, phoneNumber: value }));
  };

  const handleTimezoneChange = (value) => {
    setPreferences((prev) => ({ ...prev, timezone: value }));
  };

  const handleResetCookies = () => {
    localStorage.removeItem('cookieConsent');
    setNotificationMessage('Cookie consent reset. Refresh to see the banner.');
    setShowNotification(true);
  };

  const handleSave = () => {
    // Validate phone number before saving
    if (formData.phoneNumber && !isValidPhoneNumber(formData.phoneNumber)) {
      setNotificationMessage('Invalid phone number');
      setShowNotification(true);
      return;
    }
    // TODO: API Call to save the data
    setOriginalData(formData);
    setOriginalPreferences(preferences);
    setNotificationMessage('Profile and Preferences Saved!');
    setShowNotification(true);
  };

  const handleCancel = () => {
    setFormData(originalData);
    setPreferences(originalPreferences);
    setNotificationMessage('Changes Cancelled');
    setShowNotification(true);
  };

  // Render a loading state while user is being fetched
  if (isLoading) {
    return <div className="profile-container">Loading...</div>;
  }

  return (
    <div className="profile-container">
      <Notification
        message={notificationMessage}
        show={showNotification}
        onClose={() => setShowNotification(false)}
      />
      {user?.profilePictureUrl && (
        <img src={user.profilePictureUrl} alt="Profile" className="profile-picture" />
      )}
      <h1 className="profile-title">My Profile</h1>
      <form className="profile-form">
        <div className="form-group">
          <label htmlFor="firstName">First Name</label>
          <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="lastName">Last Name</label>
          <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="jobTitle">Job Title</label>
          <input type="text" id="jobTitle" name="jobTitle" value={formData.jobTitle} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="phoneNumber">Cell Phone (for SMS)</label>
          <PhoneInput
            id="phoneNumber"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handlePhoneChange}
            defaultCountry="US"
            international
            withCountryCallingCode
            flags={flags}
          />
        </div>
      </form>

      <div className="customization-section">
        <h3>User Customization</h3>
        <div className="customization-grid">
          <div className="form-group">
            <label htmlFor="timezone">Timezone</label>
            <TimezoneSelect
              id="timezone"
              name="timezone"
              value={preferences.timezone}
              onChange={handleTimezoneChange}
              styles={customTimezoneStyles}
            />
          </div>

          <div className="form-group">
            <label htmlFor="appearance">Appearance</label>
            <select id="appearance" name="appearance" value={preferences.appearance} onChange={handlePreferenceChange}>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
              <option value="system">System Default</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="uiDensity">UI Density</label>
            <select id="uiDensity" name="uiDensity" value={preferences.uiDensity} onChange={handlePreferenceChange}>
              <option value="comfortable">Comfortable</option>
              <option value="compact">Compact</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="dateFormat">Date Format</label>
            <select id="dateFormat" name="dateFormat" value={preferences.dateFormat} onChange={handlePreferenceChange}>
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="clockFormat">Clock Format</label>
            <select id="clockFormat" name="clockFormat" value={preferences.clockFormat} onChange={handlePreferenceChange}>
              <option value="12h">12-hour (AM/PM)</option>
              <option value="24h">24-hour</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="sidebarBehavior">Sidebar Default</label>
            <select id="sidebarBehavior" name="sidebarBehavior" value={preferences.sidebarBehavior} onChange={handlePreferenceChange}>
              <option value="expanded">Expanded</option>
              <option value="collapsed">Collapsed</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="defaultLandingTool">Default Landing Tool</label>
            <select id="defaultLandingTool" name="defaultLandingTool" value={preferences.defaultLandingTool} onChange={handlePreferenceChange}>
              <option value="home">Dashboard</option>
              {buddyToolsData.map(tool => (
                <option key={tool.id} value={tool.path}>{tool.title}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="accessibilityFontSize">Accessibility: Font Size</label>
            <select id="accessibilityFontSize" name="accessibilityFontSize" value={preferences.accessibilityFontSize} onChange={handlePreferenceChange}>
              <option value="small">Small</option>
              <option value="medium">Medium (Default)</option>
              <option value="large">Large</option>
              <option value="x-large">Extra Large</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="doNotSell" className="checkbox-label">
              <input
                type="checkbox"
                id="doNotSell"
                name="doNotSell"
                checked={preferences.doNotSell}
                onChange={(e) => setPreferences(prev => ({ ...prev, doNotSell: e.target.checked }))}
              />
              Do Not Sell My Personal Information (CCPA)
            </label>
          </div>

          <div className="form-group cookie-prefs-group">
            <label>Cookie Preferences</label>
            <button type="button" className="btn btn-secondary btn-sm" onClick={handleResetCookies}>
              Reset Cookie Consent
            </button>
          </div>
        </div>
      </div>

      <div className="client-group-section">
        <h3>Client & Group Information</h3>
        <table className="client-group-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Default Group</th>
              <th>Client-Specific Email</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Citadel Strategic Ventures</td>
              <td>Armory Admins</td>
              <td>admin-csy@citadel.com</td>
            </tr>
            <tr>
              <td>My Tech Buddy</td>
              <td>BuddyTools Users</td>
              <td>user-mtb@mytechbuddy.com</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-primary" onClick={handleSave}>Save</button>
        <button type="button" className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
      </div>

      {user && (
        <div className="identity-section">
          <h3>Identity Information</h3>
          <div className="identity-table-container">
            <table className="identity-table">
              <thead>
                <tr>
                  <th>Identity Provider</th>
                  <th>Ultimate Identity Provider</th>
                  <th>Authentication Method</th>
                  <th>User ID</th>
                  <th>Created At</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{user.idp || 'WorkOS'}</td>
                  <td>{user.ultimateIdp || 'N/A'}</td>
                  <td>{user.authenticationMethod || 'N/A'}</td>
                  <td>{user.id}</td>
                  <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
